import { z } from 'zod';
// Сообщения проверки — на русском (и в браузере, и на сервере).
z.config(z.locales.ru());
export const fieldTypes = ['text', 'textarea', 'email', 'phone', 'number', 'date', 'daterange', 'select', 'checkbox', 'boolean', 'file', 'section'];
/** Типы, которые не принимают ответ (оформление формы). */
export const displayOnlyTypes = ['section'];
export const isInputField = (field) => !displayOnlyTypes.includes(field.type);
/** Форматы проверки короткого текста. */
export const textFormats = ['none', 'digits', 'postal_code', 'passport_series', 'passport_number', 'division_code', 'snils', 'inn'];
export const textFormatMeta = {
    none: { label: 'Любой текст', placeholder: '', inputmode: 'text' },
    digits: { label: 'Только цифры', placeholder: '', inputmode: 'numeric' },
    postal_code: { label: 'Почтовый индекс', placeholder: '123456', inputmode: 'numeric' },
    passport_series: { label: 'Серия паспорта РФ', placeholder: '12 34', inputmode: 'numeric' },
    passport_number: { label: 'Номер паспорта РФ', placeholder: '123456', inputmode: 'numeric' },
    division_code: { label: 'Код подразделения', placeholder: '123-456', inputmode: 'numeric' },
    snils: { label: 'СНИЛС', placeholder: '123-456-789 01', inputmode: 'numeric' },
    inn: { label: 'ИНН', placeholder: '10 или 12 цифр', inputmode: 'numeric' }
};
const onlyDigits = (value) => value.replace(/\D/g, '');
function validSnils(value) {
    const digits = onlyDigits(value);
    if (digits.length !== 11)
        return false;
    const number = digits.slice(0, 9);
    // Контрольное число проверяется для номеров больше 001-001-998.
    if (Number(number) <= 1001998)
        return true;
    const sum = [...number].reduce((acc, digit, index) => acc + Number(digit) * (9 - index), 0);
    const control = sum % 101 === 100 ? 0 : sum % 101;
    return control === Number(digits.slice(9));
}
function validInn(value) {
    const digits = [...onlyDigits(value)].map(Number);
    const check = (weights) => weights.reduce((acc, weight, index) => acc + weight * digits[index], 0) % 11 % 10;
    if (digits.length === 10)
        return check([2, 4, 10, 3, 5, 9, 4, 6, 8]) === digits[9];
    if (digits.length === 12)
        return check([7, 2, 4, 10, 3, 5, 9, 4, 6, 8]) === digits[10] && check([3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8]) === digits[11];
    return false;
}
/** Проверяет формат значения; возвращает текст ошибки или null. */
export function validateTextFormat(format, raw) {
    const value = raw.trim();
    switch (format ?? 'none') {
        case 'digits': return /^\d+$/.test(value) ? null : 'Допустимы только цифры';
        case 'postal_code': return /^\d{6}$/.test(value) ? null : 'Индекс — 6 цифр';
        case 'passport_series': return /^\d{2}\s?\d{2}$/.test(value) ? null : 'Серия паспорта — 4 цифры';
        case 'passport_number': return /^\d{6}$/.test(value) ? null : 'Номер паспорта — 6 цифр';
        case 'division_code': return /^\d{3}-?\d{3}$/.test(value) ? null : 'Код подразделения в формате 123-456';
        case 'snils': return /^\d{3}-?\d{3}-?\d{3}[\s-]?\d{2}$/.test(value) && validSnils(value) ? null : 'Проверьте номер СНИЛС';
        case 'inn': return validInn(value) && /^[\d\s]+$/.test(value) ? null : 'Проверьте ИНН';
        default: return null;
    }
}
export function validatePhone(raw) {
    const value = raw.trim();
    const digits = onlyDigits(value);
    return /^\+?[\d\s()-]+$/.test(value) && digits.length >= 10 && digits.length <= 15 ? null : 'Введите телефон, например +7 900 123-45-67';
}
const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;
/** Дата YYYY-MM-DD → ДД.ММ.ГГГГ (остальное без изменений). */
export const formatDateRu = (value) => (isoDatePattern.test(value) ? value.split('-').reverse().join('.') : value);
/** Период «с … по …» хранится как ['YYYY-MM-DD', 'YYYY-MM-DD']. */
export function validateDateRange(value, required) {
    if (!Array.isArray(value) || value.length !== 2 || value.some((item) => typeof item !== 'string'))
        return 'Недопустимое значение';
    const [from, to] = value;
    if (!from && !to)
        return required ? 'Обязательное поле' : null;
    if (!from || !to)
        return 'Укажите обе даты: «с» и «по»';
    if (!isoDatePattern.test(from) || !isoDatePattern.test(to))
        return 'Некорректная дата';
    if (from > to)
        return 'Дата «по» раньше даты «с»';
    return null;
}
/** Ответ на поле одной строкой для таблицы, Excel и писем; пустой ответ — ''. */
export function formatAnswerText(field, value) {
    if (field.type === 'boolean')
        return value === true ? 'Да' : value === false ? 'Нет' : '';
    if (value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length))
        return '';
    if (field.type === 'daterange' && Array.isArray(value)) {
        const [from, to] = value.map(String);
        return from || to ? `с ${formatDateRu(from) || '…'} по ${formatDateRu(to) || '…'}` : '';
    }
    if (Array.isArray(value))
        return value.map(String).join(', ');
    if (field.type === 'date')
        return formatDateRu(String(value));
    return String(value);
}
/** Проверка значения одного поля (общая для браузера и сервера). Файлы проверяются отдельно. */
export function validateFieldValue(field, value) {
    if (!isInputField(field) || field.type === 'file')
        return null;
    // Одна галочка: true — отмечена; обязательная должна быть отмечена.
    if (field.type === 'boolean') {
        if (value === true)
            return null;
        if (value === undefined || value === null || value === false)
            return field.required ? 'Отметьте, чтобы продолжить' : null;
        return 'Недопустимое значение';
    }
    const empty = value === undefined || value === null || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0);
    if (empty)
        return field.required ? 'Обязательное поле' : null;
    if (field.type === 'daterange')
        return validateDateRange(value, field.required);
    if (field.type === 'checkbox') {
        if (!Array.isArray(value) || value.some((item) => !field.options.includes(String(item))))
            return 'Недопустимое значение';
        return null;
    }
    if (typeof value !== 'string' && typeof value !== 'number')
        return 'Недопустимое значение';
    const text = String(value);
    if (text.length > 5000)
        return 'Слишком длинный ответ';
    if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text.trim()))
        return 'Некорректный email';
    if (field.type === 'phone')
        return validatePhone(text);
    if (field.type === 'number' && !Number.isFinite(Number(text)))
        return 'Введите число';
    if (field.type === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(text))
        return 'Некорректная дата';
    if (field.type === 'select' && !field.options.includes(text))
        return 'Недопустимое значение';
    if (field.type === 'text')
        return validateTextFormat(field.format, text);
    return null;
}
const richLinkPattern = /\[([^\]\n]{1,300})\]\((https?:\/\/[^\s)]{1,2000}|mailto:[^\s)]{1,320})\)|(https?:\/\/[^\s<>()]{1,2000}[^\s<>().,;:!?»"'])/g;
export function parseRichText(text) {
    const parts = [];
    let last = 0;
    for (const match of text.matchAll(richLinkPattern)) {
        const index = match.index ?? 0;
        if (index > last)
            parts.push({ type: 'text', text: text.slice(last, index) });
        if (match[3])
            parts.push({ type: 'link', text: match[3], href: match[3] });
        else
            parts.push({ type: 'link', text: match[1], href: match[2] });
        last = index + match[0].length;
    }
    if (last < text.length)
        parts.push({ type: 'text', text: text.slice(last) });
    return parts;
}
/** Текст без разметки ссылок: «текст (адрес)». */
export function richTextToPlain(text, withUrls = true) {
    return parseRichText(text).map((part) => part.type === 'link' && part.text !== part.href ? (withUrls ? `${part.text} (${part.href})` : part.text) : part.text).join('');
}
export const consentSchema = z.object({
    id: z.string().min(1).max(80),
    text: z.string().trim().min(1).max(1000),
    required: z.boolean().default(true),
    linkLabel: z.string().trim().max(160).optional().default(''),
    linkUrl: z.string().trim().max(2048).optional().default('').refine((value) => !value || /^https?:\/\//i.test(value), 'Ссылка должна начинаться с http:// или https://'),
    // Полный текст согласия, который показывается в отдельном окне с кнопками «Принимаю / Не принимаю».
    fullText: z.string().trim().max(20000).optional().default(''),
    // Как даётся согласие: галочкой без окна или через окно с полным текстом («Принимаю / Не принимаю»).
    presentation: z.enum(['checkbox', 'dialog']).optional().default('checkbox'),
    // Для окна: кнопка «Принимаю» доступна только после прокрутки полного текста до конца.
    requireReading: z.boolean().optional().default(true)
}).superRefine((value, ctx) => {
    if (value.presentation === 'dialog' && !value.fullText)
        ctx.addIssue({ code: 'custom', path: ['fullText'], message: 'Для согласия через окно заполните полный текст' });
});
export const OPTION_MAX_LENGTH = 1000;
export const MAX_OPTIONS = 150;
export const FIELD_LABEL_MAX = 1000;
export const formFieldSchema = z.object({
    id: z.string().min(1).max(80),
    type: z.enum(fieldTypes),
    label: z.string().trim().min(1, 'Заполните название поля').max(FIELD_LABEL_MAX, `Название поля длиннее ${FIELD_LABEL_MAX} символов`),
    description: z.string().trim().max(5000, 'Подпись под вопросом длиннее 5000 символов').optional().default(''),
    required: z.boolean().default(false),
    // Варианты бывают на нескольких языках сразу (русский / английский / китайский) — до 1000 символов на вариант.
    options: z.array(z.string().trim().min(1, 'Пустой вариант ответа').max(OPTION_MAX_LENGTH, `Вариант ответа длиннее ${OPTION_MAX_LENGTH} символов`)).max(MAX_OPTIONS, `Не более ${MAX_OPTIONS} вариантов`).optional().default([]),
    format: z.enum(textFormats).optional().default('none'),
    maxFileSizeMb: z.number().int().min(1).max(4096).optional(),
    maxFiles: z.number().int().min(1).max(20).optional().default(1),
    acceptedFileTypes: z.array(z.string().max(100)).max(20).optional().default([])
});
export const notificationSettingsSchema = z.object({
    enabled: z.boolean().default(false),
    emails: z.array(z.string().trim().toLowerCase().pipe(z.email('Некорректный email').max(320))).max(10, 'Не более 10 адресов').default([]),
    // Прикладывать файлы ответа к письму (если их общий размер не больше EMAIL_ATTACHMENTS_MAX_MB).
    attachFiles: z.boolean().optional().default(false)
}).superRefine((value, ctx) => {
    if (value.enabled && !value.emails.length)
        ctx.addIssue({ code: 'custom', path: ['emails'], message: 'Укажите хотя бы один email для отправки ответов' });
});
/** Условия автоответа: если ответ на поле подходит, в письмо добавляется абзац. */
export const autoReplyOperators = ['equals', 'not_equals', 'contains', 'filled', 'empty'];
export const autoReplyOperatorLabels = {
    equals: 'равно', not_equals: 'не равно', contains: 'содержит', filled: 'заполнено / отмечено', empty: 'не заполнено'
};
export const autoReplyRuleSchema = z.object({
    id: z.string().trim().min(1).max(100),
    fieldId: z.string().trim().min(1).max(100),
    operator: z.enum(autoReplyOperators),
    value: z.string().trim().max(500).optional().default(''),
    text: z.string().trim().min(1, 'Заполните текст для условия автоответа').max(5000)
});
/** Автоответ участнику на email, который он указал в форме. */
export const autoReplySchema = z.object({
    enabled: z.boolean().default(false),
    emailFieldId: z.string().trim().max(100).optional().default(''),
    subject: z.string().trim().max(200).optional().default('Ваша заявка принята'),
    body: z.string().trim().max(10000).optional().default(''),
    includeAnswers: z.boolean().optional().default(false),
    rules: z.array(autoReplyRuleSchema).max(30, 'Не более 30 условий').optional().default([])
});
export const defaultAutoReply = () => ({ enabled: false, emailFieldId: '', subject: 'Ваша заявка принята', body: '', includeAnswers: false, rules: [] });
export const formDefinitionSchema = z.object({
    title: z.string().trim().min(1).max(160),
    description: z.string().trim().max(5000).optional().default(''),
    slug: z.string().trim().regex(/^[a-z0-9][a-z0-9-]{2,79}$/),
    status: z.enum(['draft', 'published', 'closed']).default('draft'),
    fields: z.array(formFieldSchema).min(1).max(150),
    consents: z.array(consentSchema).max(20).optional().default([]),
    notifications: notificationSettingsSchema.optional().default({ enabled: false, emails: [], attachFiles: false }),
    autoReply: autoReplySchema.optional().default(defaultAutoReply)
}).superRefine((form, ctx) => {
    const reply = form.autoReply;
    if (!reply?.enabled)
        return;
    const emailField = form.fields.find((field) => field.id === reply.emailFieldId);
    if (!emailField || emailField.type !== 'email')
        ctx.addIssue({ code: 'custom', path: ['autoReply', 'emailFieldId'], message: 'Автоответ: выберите поле Email, на которое отправлять письмо' });
    if (!reply.subject)
        ctx.addIssue({ code: 'custom', path: ['autoReply', 'subject'], message: 'Автоответ: укажите тему письма' });
    if (!reply.body && !reply.rules.length)
        ctx.addIssue({ code: 'custom', path: ['autoReply', 'body'], message: 'Автоответ: напишите текст письма' });
    reply.rules.forEach((rule, index) => {
        const field = form.fields.find((item) => item.id === rule.fieldId);
        if (!field || !isInputField(field) || field.type === 'file')
            ctx.addIssue({ code: 'custom', path: ['autoReply', 'rules', index, 'fieldId'], message: `Автоответ: условие ${index + 1} ссылается на поле, которого нет в форме` });
        if ((rule.operator === 'equals' || rule.operator === 'not_equals' || rule.operator === 'contains') && !rule.value)
            ctx.addIssue({ code: 'custom', path: ['autoReply', 'rules', index, 'value'], message: `Автоответ: в условии ${index + 1} укажите значение` });
    });
});
/** Предел общего размера вложений в письме: почтовые серверы обычно принимают до 25 МБ с учётом кодирования (+37%). */
export const EMAIL_ATTACHMENTS_MAX_MB = 15;
const hasAnswerValue = (value) => !(value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length));
/**
 * Столбцы таблицы ответов и Excel: текущие поля формы, затем удалённые поля, у которых в анкетах есть ответы или файлы
 * (с пометкой «(удалено)»). Ответы без записи об удалённом поле (поле удалили до появления этой функции) — «Удалённое поле N».
 */
export function responseColumns(fields, removed, answers, fileFieldIds = []) {
    const current = fields.filter(isInputField).map((field) => ({ id: field.id, label: field.label, type: field.type, removed: false }));
    const currentIds = new Set(current.map((column) => column.id));
    const withAnswers = new Set();
    for (const item of answers)
        for (const [key, value] of Object.entries(item ?? {}))
            if (hasAnswerValue(value))
                withAnswers.add(key);
    const withFiles = new Set(fileFieldIds);
    const hasData = (id) => withAnswers.has(id) || withFiles.has(id);
    const old = removed.filter((field) => !currentIds.has(field.id) && hasData(field.id))
        .map((field) => ({ id: field.id, label: `${field.label} (удалено)`, type: field.type, removed: true }));
    const known = new Set([...currentIds, ...removed.map((field) => field.id)]);
    const orphanIds = [...new Set([...withAnswers, ...withFiles])].filter((id) => !known.has(id));
    const orphans = orphanIds.map((id, index) => ({ id, label: `Удалённое поле ${index + 1}`, type: withFiles.has(id) && !withAnswers.has(id) ? 'file' : 'text', removed: true }));
    return [...current, ...old, ...orphans];
}
const plainKey = (text) => text.replace(/\s+/g, ' ').trim().toLowerCase();
/** Выполняется ли условие автоответа для ответа на поле. Сравнение без учёта регистра и пробелов по краям. */
export function autoReplyRuleMatches(field, rule, value) {
    const text = plainKey(formatAnswerText(field, value));
    const filled = field.type === 'boolean' ? value === true : text !== '';
    if (rule.operator === 'filled')
        return filled;
    if (rule.operator === 'empty')
        return !filled;
    const target = plainKey(rule.value);
    // Для «нескольких вариантов» — сравнение с каждым выбранным вариантом.
    const items = field.type === 'checkbox' && Array.isArray(value) ? value.map((item) => plainKey(String(item))) : [text];
    if (rule.operator === 'equals')
        return items.includes(target);
    if (rule.operator === 'not_equals')
        return !items.includes(target);
    return text.includes(target);
}
/** Подстановки автоответа: {Подпись поля} → ответ участника, плюс служебные ({Название формы}, {Дата}). */
export function autoReplyPlaceholderValues(fields, answers, extra = {}) {
    const values = new Map();
    for (const field of fields) {
        if (!isInputField(field) || field.type === 'file')
            continue;
        const key = plainKey(richTextToPlain(field.label, false));
        if (key && !values.has(key))
            values.set(key, formatAnswerText(field, answers[field.id]));
    }
    for (const [name, value] of Object.entries(extra))
        values.set(plainKey(name), value);
    return values;
}
const answerPlaceholderPattern = /\{([^{}\n]{1,120})\}/g;
export function fillAutoReplyPlaceholders(text, values) {
    return text.replace(answerPlaceholderPattern, (whole, name) => (values.has(plainKey(name)) ? values.get(plainKey(name)) : whole));
}
/** Подстановки в тексте, которым не соответствует ни одно поле (опечатка или поле переименовали). */
export function unknownAutoReplyPlaceholders(text, fields, extraNames = []) {
    const known = autoReplyPlaceholderValues(fields, {}, Object.fromEntries(extraNames.map((name) => [name, ''])));
    return [...new Set([...text.matchAll(answerPlaceholderPattern)].map((m) => m[1]).filter((name) => !known.has(plainKey(name))))];
}
export const AUTO_REPLY_EXTRA_PLACEHOLDERS = ['Название формы', 'Дата'];
/**
 * Содержимое автоответа: адрес, тема и абзацы (основной текст + абзацы сработавших условий).
 * Разметка ссылок разбирается ДО подстановки ответов, поэтому ответы участника не становятся ссылками.
 */
export function composeAutoReply(form, answers, submittedAt) {
    const reply = form.autoReply;
    if (!reply?.enabled)
        return null;
    const to = answers[reply.emailFieldId];
    if (typeof to !== 'string' || to.length > 320 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim()))
        return null;
    const values = autoReplyPlaceholderValues(form.fields, answers, { 'Название формы': form.title, 'Дата': submittedAt });
    const sources = [reply.body, ...reply.rules.filter((rule) => {
            const field = form.fields.find((item) => item.id === rule.fieldId);
            return field ? autoReplyRuleMatches(field, rule, answers[rule.fieldId]) : false;
        }).map((rule) => rule.text)].filter(Boolean);
    const paragraphs = sources.map((source) => parseRichText(source).map((part) => (part.type === 'link' ? { ...part, text: fillAutoReplyPlaceholders(part.text, values) } : { type: 'text', text: fillAutoReplyPlaceholders(part.text, values) })));
    const subject = fillAutoReplyPlaceholders(reply.subject, values).replace(/[\r\n]+/g, ' ').trim().slice(0, 300);
    return { to: to.trim(), subject, paragraphs, includeAnswers: reply.includeAnswers };
}
/** Понятное описание ошибки проверки формы: «Поле «Направление», вариант 3: …». */
export function describeFormIssue(issue, form) {
    const [section, index, key, subIndex] = issue.path;
    if (section === 'fields' && typeof index === 'number') {
        const label = richTextToPlain(String(form?.fields?.[index]?.label ?? ''), false).slice(0, 60) || `№${index + 1}`;
        const where = key === 'options' && typeof subIndex === 'number' ? `, вариант ${subIndex + 1}` : '';
        return `Поле «${label}»${where}: ${issue.message}`;
    }
    if (section === 'consents' && typeof index === 'number')
        return `Согласие ${index + 1}: ${issue.message}`;
    if (section === 'title')
        return `Название формы: ${issue.message}`;
    if (section === 'slug')
        return 'Адрес формы: только латиница в нижнем регистре, цифры и дефис, от 3 до 80 символов';
    return issue.message;
}
